// Gemeinsame Bausteine der Tool-Seiten (Konverter, Viewer, Formatter, Validator)
import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { LuArrowLeftRight, LuEye, LuWandSparkles, LuShieldCheck, LuUpload, LuLock, LuUserX, LuCircleCheck } from "react-icons/lu";

export const TOOLS = [
  { key: "convert",  to: "/edi-konverter",     name: "Konverter", tab: "Konvertieren", icon: LuArrowLeftRight,
    desc: "EDIFACT in Excel, CSV, XML oder JSON umwandeln." },
  { key: "view",     to: "/edifact-viewer",    name: "Viewer",    tab: "Viewer",       icon: LuEye,
    desc: "Bestellungen, Rechnungen und Lieferavise lesbar anzeigen." },
  { key: "format",   to: "/edifact-formatter", name: "Formatter", tab: "Formatieren",  icon: LuWandSparkles,
    desc: "Segmente bearbeiten und sauber formatiert exportieren." },
  { key: "validate", to: "/edifact-validator", name: "Validator", tab: "Validator",    icon: LuShieldCheck,
    desc: "Syntax, Struktur und Zähler auf Fehler prüfen." },
];

export function ToolTabs() {
  return (
    <nav className="tool-tabs" aria-label="EDIFACT-Tools">
      {TOOLS.map(t => (
        <NavLink key={t.key} to={t.to} className={({ isActive }) => "tool-tab" + (isActive ? " active" : "")}>
          <t.icon size={16} aria-hidden="true" />
          {t.tab}
        </NavLink>
      ))}
    </nav>
  );
}

export function ToolHero({ tool, title, description, intro, children }) {
  const Icon = TOOLS.find(x => x.key === tool)?.icon;
  return (
    <section className="page-hero tool-hero">
      <div className="container">
        <div className="tool-hero-head">
          {Icon && <span className="icon-tile icon-tile-lg" style={{ marginBottom: 16 }}><Icon size={24} aria-hidden="true" /></span>}
          <h1 className="h1">{title}</h1>
          <p className="lead">{description}</p>
          <ToolTabs />
          {intro && <p className="tool-intro">{intro}</p>}
        </div>
        <div className="tool-body">{children}</div>
      </div>
    </section>
  );
}

/**
 * Ergebnis-Bereich nach dem Laden einer Datei ins Bild holen – das Ergebnis erscheint
 * unterhalb der Ablage und würde sonst (v. a. am Handy) leicht übersehen.
 * trigger: ändert sich bei jeder erfolgreich geladenen Datei. Gibt die ref für den Ergebnis-Container zurück.
 */
export function useResultScroll(trigger) {
  const ref = useRef(null);
  useEffect(() => {
    if (!trigger || !ref.current) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [trigger]);
  return ref;
}

/**
 * Datei-Ablage (Klick, Tastatur oder Drag & Drop).
 * Gibt die gewählte Datei an onFile weiter – Prüfungen passieren in der jeweiligen Seite.
 * accept: ohne Angabe sind alle Dateien wählbar – EDIFACT hat oft Endungen wie .001 oder .dat,
 *         ob es wirklich EDIFACT ist, prüft die Seite anhand des Inhalts.
 * loadedName: nach dem Laden schrumpft die Ablage auf eine schlanke Dateizeile
 * (bleibt Ablagefläche für die nächste Datei).
 */
export function Dropzone({ onFile, inputRef, accept, title = "Datei auswählen oder hierher ziehen", titleAs = "h3", hint, loadedName }) {
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;
  const [dragging, setDragging] = useState(false);
  const Title = titleAs;

  const open = () => ref.current && ref.current.click();

  return (
    <div
      className={"dropzone" + (dragging ? " is-dragging" : "") + (loadedName ? " dropzone-compact" : "")}
      onClick={open}
      onDragOver={e => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
      onDrop={e => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files?.[0]); }}
    >
      {loadedName ? (
        <>
          <span className="dropzone-done"><LuCircleCheck size={20} aria-hidden="true" /></span>
          <span className="dropzone-compact-text" role="status">
            <span><b className="dropzone-file">{loadedName}</b> geladen</span>
            <span className="dropzone-compact-hint">Neue Datei einfach hierher ziehen</span>
          </span>
        </>
      ) : (
        <>
          <span className="dropzone-icon"><LuUpload size={24} aria-hidden="true" /></span>
          <Title className="dropzone-title">{title}</Title>
          {hint && <p className="dropzone-hint">{hint}</p>}
        </>
      )}
      <button
        type="button"
        className={"btn btn-sm " + (loadedName ? "btn-secondary" : "btn-primary")}
        onClick={e => { e.stopPropagation(); open(); }}
      >
        {loadedName ? "Andere Datei auswählen" : "Datei auswählen"}
      </button>
      {!loadedName && (
        <span className="dropzone-foot">
          <span><LuLock size={14} aria-hidden="true" />Nur lokal im Browser</span>
          <span><LuUserX size={14} aria-hidden="true" />Ohne Anmeldung</span>
          <span><LuCircleCheck size={14} aria-hidden="true" />Kostenlos</span>
        </span>
      )}
      <input
        type="file"
        accept={accept}
        ref={ref}
        hidden
        onClick={e => e.stopPropagation()}
        onChange={e => { onFile(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
